import React from "react";
import { Typography, Box, Button, Chip } from "@mui/material";
import {
  Dashboard as DashboardIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useDashboard } from "../../hooks/useWidgets";
import { getDefaultDashboardLayout } from "../../constants/defaultDashboardLayout";
import HomeSection from "./HomeSection";
import { homeCardHover } from "./homeStyles";
import { colors } from "../../theme";
import {
  BRAND_COLORS,
  brandFilledButtonSx,
  brandRgba,
} from "../../utils/brandAssets";

/**
 * Slim teaser on Home — links to the full editable dashboard at /app/dashboard.
 */
const YourWidgetsSection = () => {
  const navigate = useNavigate();
  const { dashboard, isLoading } = useDashboard("home");
  const defaultLayout = getDefaultDashboardLayout("home").layout;
  const widgetCount = dashboard?.layout?.length || 0;
  const hasSavedWidgets = widgetCount > 0;
  const hasStarterWidgets = !hasSavedWidgets && defaultLayout.length > 0;
  const displayCount = hasSavedWidgets ? widgetCount : defaultLayout.length;
  const isShowingStarters =
    hasStarterWidgets || (hasSavedWidgets && dashboard?.isDefaultLayout === true);

  const getDescription = () => {
    if (isLoading) return "Loading your widgets…";
    if (hasSavedWidgets) {
      return `${widgetCount} saved widget${widgetCount === 1 ? "" : "s"} — charts, leaders, and live scores`;
    }
    if (hasStarterWidgets) {
      return `${defaultLayout.length} starter widgets ready — charts, leaders, and live scores`;
    }
    return "Add charts, leaders, and live scores to your personal dashboard";
  };

  const ctaLabel =
    hasSavedWidgets || hasStarterWidgets ? "Open dashboard" : "Customize dashboard";

  const handleOpenDashboard = () => navigate("/app/dashboard");

  return (
    <HomeSection
      title="Your dashboard"
      subtitle="Personalized stats at a glance"
    >
      <Box
        onClick={handleOpenDashboard}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleOpenDashboard();
          }
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          ...homeCardHover,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: brandRgba(BRAND_COLORS.cyan, 0.12),
            flexShrink: 0,
          }}
        >
          <DashboardIcon sx={{ color: BRAND_COLORS.cyan, fontSize: 28 }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 180 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {displayCount > 0
                ? `${displayCount} widget${displayCount === 1 ? "" : "s"}`
                : "Build your dashboard"}
            </Typography>
            {isShowingStarters && !isLoading && (
              <Chip
                label="Starter pack"
                size="small"
                sx={{
                  height: 22,
                  fontSize: "0.7rem",
                  bgcolor: brandRgba(BRAND_COLORS.cyan, 0.15),
                  color: BRAND_COLORS.cyan,
                  border: `1px solid ${colors.border}`,
                }}
              />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {getDescription()}
          </Typography>
        </Box>

        <Button
          variant="contained"
          size="small"
          endIcon={<ArrowForwardIcon />}
          onClick={(event) => {
            event.stopPropagation();
            handleOpenDashboard();
          }}
          sx={{ flexShrink: 0, ...brandFilledButtonSx }}
        >
          {ctaLabel}
        </Button>
      </Box>
    </HomeSection>
  );
};

export default YourWidgetsSection;
